import React from "react";
import { useTranslation } from "react-i18next";
import { Avatar, Box, Typography } from "@mui/material";
import color from "../../Constants/colors";
import { useTerminology } from "../../Core/useTerminology";
import { Features, UserRole } from "../../Enums/companyStatus";
import { hasPermission, hasRole } from "../../Helpers/authHelpers";
import { useLibraryQuota } from "../../Query/useComoAgent";
import { useUser } from "../../Query/useUsers";
import { useCurrentWorkspace } from "../../Query/useWorkspace";
import DashboardCard from "./DashboardCard";
import LimitGauge, { LimitGaugeProps } from "./LimitGauge";
import { useDashboardColors } from "./dashboardColors";

/** Workspace details and the user's limits (moved here from the profile page). */
const WorkspaceCard: React.FC = () => {
  const { t } = useTranslation("home");
  const terminology = useTerminology();
  const { accent, accentText, onAccent } = useDashboardColors();
  const { data: user } = useUser();
  const { data: workspaceData } = useCurrentWorkspace();
  const isAdmin = hasRole(UserRole.ADMIN);
  const can = (feature: Features) => isAdmin || hasPermission(feature);
  const showLibrary = can(Features.COMO_EXPERTS);
  const { data: libraryQuota } = useLibraryQuota(showLibrary);

  const workspace = workspaceData?.workspace ?? null;
  const workspaceIcon = workspace?.icon_image
    ? `data:image/png;base64,${workspace.icon_image}`
    : null;
  const admin = workspace?.workspace_admin;

  // Admins skip the evaluation and entity limit checks on the backend.
  const limitOf = (value: number | null | undefined) =>
    isAdmin || value === null || value === undefined ? null : value;

  const meters: LimitGaugeProps[] = [
    ...(can(Features.EVALUATION) && user
      ? [
          {
            label: t("hub.limits.evaluations"),
            used: user.evaluations ?? 0,
            limit: limitOf(user.evaluation_limit),
          },
        ]
      : []),
    ...(can(Features.ENTITY_CREATION) && user
      ? [
          {
            label: t("hub.limits.entities", { entity: terminology.plural }),
            used: user.companiesCount ?? 0,
            limit: limitOf(user.companies_limit),
          },
        ]
      : []),
    ...(showLibrary && libraryQuota
      ? [
          {
            label: t("hub.limits.library"),
            used: libraryQuota.consumed,
            limit: libraryQuota.limit < 0 ? null : libraryQuota.limit,
            unit: "MB",
          },
        ]
      : []),
  ];

  if (!workspace && meters.length === 0) return null;

  return (
    <DashboardCard
      title={workspace ? t("hub.workspace.title") : t("hub.limits.title")}
    >
      {workspace && (
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
          <Avatar
            variant="rounded"
            src={workspaceIcon ?? undefined}
            alt=""
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              fontWeight: 700,
              backgroundColor: workspaceIcon ? color.white : accent,
              color: onAccent,
              border: `1px solid ${color.gray200}`,
              "& img": { objectFit: "contain" },
            }}
          >
            {workspace.name.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontSize: 15,
                lineHeight: "20px",
                fontWeight: 700,
                color: color.gray900,
              }}
            >
              {workspace.name}
            </Typography>
            {workspace.description && (
              <Typography
                sx={{
                  mt: 0.25,
                  fontSize: 13,
                  lineHeight: "18px",
                  color: color.gray500,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {workspace.description}
              </Typography>
            )}
            {admin && (
              <Typography
                sx={{
                  mt: 0.75,
                  fontSize: 12,
                  lineHeight: "16px",
                  color: color.gray600,
                }}
              >
                {t("hub.workspace.admin")}:{" "}
                <Box
                  component="a"
                  href={`mailto:${admin.email}`}
                  sx={{ color: accentText, fontWeight: 600 }}
                >
                  {`${admin.name} ${admin.surname}`.trim()}
                </Box>
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {meters.length > 0 && (
        <Box
          sx={{
            mt: workspace ? 2 : 0,
            pt: workspace ? 2 : 0,
            borderTop: workspace ? `1px solid ${color.gray100}` : 0,
            display: "flex",
            flexDirection: "column",
            gap: 1.75,
          }}
        >
          {workspace && (
            <Typography
              sx={{
                fontSize: 11,
                lineHeight: "16px",
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: color.gray500,
              }}
            >
              {t("hub.limits.title")}
            </Typography>
          )}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${meters.length}, minmax(0, 1fr))`,
              gap: 1.5,
            }}
          >
            {meters.map((meter) => (
              <LimitGauge key={meter.label} {...meter} />
            ))}
          </Box>
          {meters.some(
            (meter) => meter.limit !== null && meter.used >= meter.limit,
          ) && (
            <Typography
              sx={{
                fontSize: 12,
                lineHeight: "16px",
                color: color.red700,
                textAlign: "center",
              }}
            >
              {t("hub.limits.reached")}
            </Typography>
          )}
        </Box>
      )}
    </DashboardCard>
  );
};

export default WorkspaceCard;
